import React, { useState, useCallback } from 'react';
import {
  View,
  ScrollView,
  SafeAreaView,
  TextInput,
  Pressable,
  StyleSheet,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { isWeb as checkIsWeb } from '../../util/platform';
import Checkbox from '../Checkbox';
import {
  heightPercentageToDP as hp,
  widthPercentageToDP as wp,
} from 'react-native-responsive-screen';
import { Feather } from '@expo/vector-icons';

import CustomText from '../CustomText';
import Util from '../../util/util';
import { showError } from '../../util/error/hookErrors';
import { fonts } from '../../style/fonts';
import AppStyles from '../../AppStyles';
import { useTranslation } from '../../i18n';

// Edit/delete window in milliseconds (15 minutes)
const EDIT_WINDOW_MS = 15 * 60 * 1000;

/**
 * ReportComments - Displays comments list and optional input for adding new comments
 *
 * Shared between ReportDetails.js and AdminReportDetails.js
 *
 * @param {Object} props
 * @param {Array} props.comments - Array of comments (strings or objects with comment/commenterIsAdmin/commentDate)
 * @param {boolean} props.isAdmin - Whether the current user is viewing as admin
 * @param {boolean} props.isClosed - Whether the report is closed (hides input if true)
 * @param {boolean} props.showInput - Whether to show the comment input (default: true when not closed)
 * @param {string} props.inputValue - Current value of the comment input
 * @param {Function} props.onInputChange - Callback when input text changes
 * @param {Function} props.onSubmit - Callback when comment is submitted
 * @param {boolean} props.submitting - Whether a comment is currently being submitted
 * @param {Object} props.webStyles - Web-specific styles from useResponsiveStyles
 * @param {Object} props.mobileStyles - Mobile-specific styles from useResponsiveStyles
 * @param {Object} props.commentsContainerStyle - Container style for comments section
 * @param {Object} props.commentContainerStyle - Container style for individual comments
 * @param {boolean} props.isWeb - Whether running on web platform
 * @param {boolean} props.alwaysShowInput - Show input even when no comments exist (for admin)
 * @param {boolean} props.adminOnlyValue - Current value of admin-only checkbox (admin only)
 * @param {Function} props.onAdminOnlyChange - Callback when admin-only checkbox changes (admin only)
 * @param {string} props.currentUserEmail - Email of the current user (for edit/delete permissions)
 * @param {boolean} props.isAnonymousSession - True for QR / logged-out sessions. Those share a
 *   single Cognito account, so comment authorship cannot identify an individual; the server
 *   rejects edits and deletes from them, and the controls are hidden to match.
 * @param {Function} props.onEditComment - Callback when a comment is edited (commentId, newText) => void
 * @param {Function} props.onDeleteComment - Callback when a comment is deleted (commentId) => void
 */
const ReportComments = ({
  comments = [],
  isAdmin = false,
  isClosed = false,
  showInput = true,
  inputValue = '',
  onInputChange,
  onSubmit,
  submitting = false,
  webStyles,
  mobileStyles,
  commentsContainerStyle,
  commentContainerStyle,
  isWeb = false,
  alwaysShowInput = false,
  adminOnlyValue = false,
  onAdminOnlyChange,
  currentUserEmail,
  isAnonymousSession = false,
  onEditComment,
  onDeleteComment,
}) => {
  const { t } = useTranslation(['reports', 'common']);

  const hasComments = comments != null && comments.length > 0;

  // Edit mode state
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [editText, setEditText] = useState('');
  const [editSubmitting, setEditSubmitting] = useState(false);

  /**
   * Determines if a comment is from an admin
   * Legacy comments are strings (always admin), new comments are objects
   */
  const isAdminComment = (comment) => {
    return typeof comment === 'string' || comment.commenterIsAdmin;
  };

  /**
   * Gets the display text from a comment (handles both string and object formats)
   */
  const getCommentText = (comment) => {
    return typeof comment === 'string' ? comment : comment.comment;
  };

  /**
   * Gets the comment date if available
   */
  const getCommentDate = (comment) => {
    if (typeof comment === 'string') return null;
    return comment.commentDate ? Util.timeConverter(comment.commentDate) : '';
  };

  /**
   * Checks if a comment is admin-only
   */
  const isAdminOnly = (comment) => {
    return typeof comment !== 'string' && comment.adminOnly === true;
  };

  /**
   * Check if a comment can be edited/deleted (within 15 min window and is author)
   */
  const canModifyComment = useCallback((comment) => {
    // Legacy comments (strings) cannot be modified
    if (typeof comment === 'string') return false;

    // QR / logged-out sessions all share one Cognito account, so the authorship rules below
    // cannot tell two anonymous users apart - every anonymous commenter looks like every
    // other one. The API rejects these edits and deletes; hide the controls to match rather
    // than offering a button that always fails.
    if (isAnonymousSession) return false;

    // Must have an ID and a timestamp
    if (!comment.id || !comment.commentDate) return false;

    // Check time window
    const now = Date.now();
    const commentAge = now - comment.commentDate;
    if (commentAge > EDIT_WINDOW_MS) return false;

    // Check if current user is the author
    if (comment.commenterIsAdmin) {
      // Admin comment - commenter is their email
      return comment.commenter === currentUserEmail;
    } else {
      // Non-admin-flagged comment. This includes:
      // 1. Reporter comments (commenter is display name)
      // 2. Admin-who-is-the-reporter comments (commenterIsAdmin stored as false)
      // For case 2, commenter is still the admin's email, so check that first.
      // For case 1, non-admin users only see their own reports, so all reporter
      // comments on a report they can view are theirs.
      return comment.commenter === currentUserEmail || !isAdmin;
    }
  }, [currentUserEmail, isAdmin, isAnonymousSession]);

  /**
   * Calculate time remaining in edit window
   */
  const getTimeRemaining = useCallback((comment) => {
    if (typeof comment === 'string' || !comment.commentDate) return null;
    const elapsed = Date.now() - comment.commentDate;
    const remaining = EDIT_WINDOW_MS - elapsed;
    if (remaining <= 0) return null;
    const minutes = Math.ceil(remaining / 60000);
    return `${minutes}m left to edit`;
  }, []);

  /**
   * Handle edit button click
   */
  const handleEditStart = useCallback((comment) => {
    setEditingCommentId(comment.id);
    setEditText(comment.comment);
  }, []);

  /**
   * Handle edit cancel
   */
  const handleEditCancel = useCallback(() => {
    setEditingCommentId(null);
    setEditText('');
  }, []);

  /**
   * Handle edit save
   */
  const handleEditSave = useCallback(async () => {
    if (!editingCommentId || !editText.trim() || !onEditComment) return;

    setEditSubmitting(true);
    try {
      await onEditComment(editingCommentId, editText.trim());
      setEditingCommentId(null);
      setEditText('');
    } catch (error) {
      showError(error.message || t('comments.editFailed'));
    }
    setEditSubmitting(false);
  }, [editingCommentId, editText, onEditComment]);

  /**
   * Handle delete button click with confirmation
   */
  const handleDelete = useCallback((comment) => {
    if (!comment.id || !onDeleteComment) return;

    const doDelete = async () => {
      try {
        await onDeleteComment(comment.id);
      } catch (error) {
        showError(error.message || t('comments.deleteFailed'));
      }
    };

    if (checkIsWeb()) {
      if (window.confirm(t('comments.deleteConfirm'))) {
        doDelete();
      }
    } else {
      Alert.alert(
        t('comments.deleteTitle'),
        t('comments.deleteConfirm'),
        [
          { text: t('common:actions.cancel'), style: 'cancel' },
          { text: t('common:actions.delete'), style: 'destructive', onPress: doDelete },
        ],
      );
    }
  }, [onDeleteComment]);

  /**
   * Filter out admin-only comments for non-admin viewers
   */
  const visibleComments = comments.filter((comment) => {
    if (isAdminOnly(comment) && !isAdmin) {
      return false;
    }
    return true;
  });

  /**
   * Group comments by type (public vs admin-only)
   */
  const publicComments = visibleComments.filter((comment) => !isAdminOnly(comment));
  const adminOnlyComments = visibleComments.filter((comment) => isAdminOnly(comment));

  /**
   * Render a single comment
   */
  const renderComment = (comment, index) => {
    const adminComment = isAdminComment(comment);
    const adminOnlyComment = isAdminOnly(comment);
    const canModify = canModifyComment(comment);
    const isEditing = editingCommentId === comment.id;
    const timeRemaining = canModify ? getTimeRemaining(comment) : null;
    const wasEdited = typeof comment !== 'string' && comment.lastEditedAt;

    return (
      <View
        key={index}
        style={[
          commentContainerStyle,
          !adminComment
            ? isWeb
              ? webStyles.secondaryCommentContainer
              : mobileStyles.secondaryCommentContainer
            : {},
        ]}
      >
        <View
          style={{
            flex: 1,
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: adminComment ? 'flex-start' : 'flex-end',
            flexWrap: 'wrap',
          }}
        >
          <CustomText
            style={
              adminComment
                ? webStyles.primaryCommentUserName
                : isWeb
                  ? webStyles.secondaryCommentUserName
                  : mobileStyles.secondaryCommentUserName
            }
          >
            {adminComment ? t('comments.roleAdmin') : t('comments.roleReporter')}
          </CustomText>
          {typeof comment !== 'string' && getCommentDate(comment) && (
            <>
              <CustomText
                style={{ marginHorizontal: 6 }}
                font="regular"
                size={12}
                color={AppStyles.colorSet.textMuted}
              >
                ·
              </CustomText>
              <CustomText
                style={
                  adminComment
                    ? webStyles.primaryCommentDateTime
                    : isWeb
                      ? webStyles.secondaryCommentDateTime
                      : mobileStyles.secondaryCommentDateTime
                }
              >
                {getCommentDate(comment)}
              </CustomText>
            </>
          )}
          {wasEdited && (
            <>
              <CustomText
                style={{ marginHorizontal: 6 }}
                font="regular"
                size={12}
                color={AppStyles.colorSet.textMuted}
              >
                ·
              </CustomText>
              <CustomText
                font="regular"
                size={11}
                color={AppStyles.colorSet.textMuted}
              >
                {t('comments.edited')}
              </CustomText>
            </>
          )}
          {adminOnlyComment && isAdmin && (
            <>
              <CustomText
                style={{ marginHorizontal: 6 }}
                font="regular"
                size={12}
                color={AppStyles.colorSet.textMuted}
              >
                ·
              </CustomText>
              <View style={styles.adminOnlyBadge}>
                <CustomText
                  font="medium"
                  size={11}
                  color="#92400e"
                >
                  {t('comments.adminOnly')}
                </CustomText>
              </View>
            </>
          )}
          {/* Edit/Delete actions */}
          {canModify && !isEditing && onEditComment && onDeleteComment && (
            <View style={styles.commentActions}>
              <Pressable
                onPress={() => handleEditStart(comment)}
                style={({ hovered, focused, pressed }) => [
                  styles.commentActionButton,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.commentActionButtonHovered,
                  pressed && { opacity: 0.6 },
                ]}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Feather name="edit-2" size={14} color={AppStyles.colorSet.textMuted} />
              </Pressable>
              <Pressable
                onPress={() => handleDelete(comment)}
                style={({ hovered, focused, pressed }) => [
                  styles.commentActionButton,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.commentActionButtonHovered,
                  pressed && { opacity: 0.6 },
                ]}
                hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
              >
                <Feather name="trash-2" size={14} color={AppStyles.colorSet.error} />
              </Pressable>
            </View>
          )}
        </View>

        {/* Time remaining indicator */}
        {timeRemaining && !isEditing && (
          <CustomText
            font="regular"
            size={10}
            color={AppStyles.colorSet.textMuted}
            style={{ marginTop: 2 }}
          >
            {timeRemaining}
          </CustomText>
        )}

        {/* Comment text or edit input */}
        {isEditing ? (
          <View style={styles.editContainer}>
            <TextInput
              value={editText}
              onChangeText={setEditText}
              multiline
              style={styles.editInput}
              autoFocus
              editable={!editSubmitting}
            />
            <View style={styles.editActions}>
              <Pressable
                onPress={handleEditCancel}
                style={({ hovered, focused, pressed }) => [
                  styles.editButton,
                  styles.editButtonCancel,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.editButtonHovered,
                  pressed && { opacity: 0.8 },
                ]}
                disabled={editSubmitting}
              >
                <CustomText font="medium" size={12} color={AppStyles.colorSet.textSecondary}>
                  {t('common:actions.cancel')}
                </CustomText>
              </Pressable>
              <Pressable
                onPress={handleEditSave}
                style={({ hovered, focused, pressed }) => [
                  styles.editButton,
                  styles.editButtonSave,
                  { cursor: 'pointer' },
                  (hovered || focused) && styles.editButtonHovered,
                  pressed && { opacity: 0.8 },
                ]}
                disabled={editSubmitting || !editText.trim()}
              >
                {editSubmitting ? (
                  <ActivityIndicator size="small" color="#000" />
                ) : (
                  <CustomText font="medium" size={12} color="#000">
                    {t('common:actions.save')}
                  </CustomText>
                )}
              </Pressable>
            </View>
          </View>
        ) : (
          <View
            style={[
              styles.orangeContainer2,
              adminComment
                ? webStyles.primaryComment
                : isWeb
                  ? webStyles.secondaryComment
                  : mobileStyles.secondaryComment,
            ]}
          >
            <ScrollView style={{}} contentContainerStyle={{}}>
              <CustomText font="regular" size={15} color={'#000'}>
                {getCommentText(comment)}
              </CustomText>
            </ScrollView>
          </View>
        )}
      </View>
    );
  };

  return (
    <>
      {/* Comments List */}
      <SafeAreaView style={commentsContainerStyle}>
        {visibleComments.length > 0 ? (
          <>
            {/* Public Comments Section */}
            {publicComments.length > 0 && (
              <View style={styles.commentGroup}>
                <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textSecondary} style={{ marginBottom: 12 }}>
                  {t('comments.discussionCount', { count: publicComments.length })}
                </CustomText>
                {publicComments.map((comment, index) => renderComment(comment, `public-${index}`))}
              </View>
            )}

            {/* Admin-Only Comments Section */}
            {adminOnlyComments.length > 0 && isAdmin && (
              <View style={[styles.commentGroup, styles.adminOnlyGroup]}>
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginBottom: 12 }}>
                  <Feather name="lock" size={14} color={AppStyles.colorSet.textMuted} />
                  <CustomText font="semiBold" size={14} color={AppStyles.colorSet.textSecondary}>
                    {t('comments.adminNotesCount', { count: adminOnlyComments.length })}
                  </CustomText>
                </View>
                {adminOnlyComments.map((comment, index) => renderComment(comment, `admin-${index}`))}
              </View>
            )}
          </>
        ) : (
          <CustomText
            style={{
              textAlign: 'center',
              marginTop: hp(0),
              marginBottom: hp(0),
              paddingVertical: hp(0),
            }}
            font="medium"
            size={15}
            color={AppStyles.colorSet.textMuted}
          >
            {t('comments.empty')}
          </CustomText>
        )}
      </SafeAreaView>

      {/* Comment Input */}
      {showInput && !isClosed && (hasComments || alwaysShowInput) && (
        <SafeAreaView style={{ marginBottom: hp(2.5) }}>
          <View style={styles.orangeContainer}>
            <TextInput
              allowFontScaling={true}
              editable={!submitting}
              multiline
              numberOfLines={3}
              onChangeText={onInputChange}
              value={inputValue}
              placeholder={t('comments.placeholder')}
              placeholderTextColor={AppStyles.colorSet.textMuted}
              style={styles.textInput}
            />
          </View>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', marginTop: hp(1) }}>
            {/* Admin-only checkbox (only shown for admins) */}
            {isAdmin && onAdminOnlyChange ? (
              <View style={styles.adminOnlyCheckboxRow}>
                <Checkbox
                  selected={adminOnlyValue}
                  onChangeValue={onAdminOnlyChange}
                  label={t('comments.adminOnlyLabel')}
                />
                <View style={styles.adminOnlyCheckboxText}>
                  <CustomText font="medium" size={13} color={AppStyles.colorSet.textPrimary}>
                    {t('comments.adminOnlyLower')}
                  </CustomText>
                  <CustomText font="regular" size={11} color={AppStyles.colorSet.textMuted}>
                    {t('comments.hiddenFromReporter')}
                  </CustomText>
                </View>
              </View>
            ) : (
              <View />
            )}
            <Pressable onPress={onSubmit} disabled={submitting}>
              {({ hovered, focused }) => (
                <View
                  style={[
                    styles.buttonSmall,
                    submitting && styles.buttonDisabled,
                    !submitting && (hovered || focused) && styles.buttonSmallHovered,
                  ]}
                >
                  {submitting ? (
                    <ActivityIndicator size="small" color="#000" />
                  ) : (
                    <CustomText font="medium" size={14} color={'#000'}>
                      {t('comments.send')}
                    </CustomText>
                  )}
                </View>
              )}
            </Pressable>
          </View>
        </SafeAreaView>
      )}
    </>
  );
};

const styles = StyleSheet.create({
  // V3: Comment input container
  orangeContainer: {
    borderRadius: 12,
    backgroundColor: '#F5F5F5',
    alignItems: 'center',
    paddingVertical: 10,
    paddingHorizontal: wp(3.2),
    marginTop: hp(1),
    maxHeight: hp(40),
    minHeight: hp(17.8),
    borderColor: '#E5E5E5',
    borderWidth: 1,
  },
  // V3: Base comment bubble - transparent so primaryComment/secondaryComment colors show through
  orangeContainer2: {
    borderRadius: 8,
    backgroundColor: 'transparent',
    paddingVertical: 4,
    paddingHorizontal: wp(1),
    marginTop: hp(0.5),
    borderWidth: 0,
  },
  textInput: {
    width: '100%',
    fontFamily: fonts.POPPINS.medium,
    fontSize: 16,
    color: '#000',
    flex: 1,
  },
  // V3: Submit button (full width)
  button: {
    cursor: 'pointer',
    backgroundColor: '#FFC451',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 12,
    height: hp(5.5),
    width: '100%',
    alignSelf: 'center',
    marginTop: hp(1.5),
  },
  // V3: Small submit button (right-aligned)
  buttonSmall: {
    cursor: 'pointer',
    backgroundColor: '#FFC451',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: 8,
    paddingVertical: 10,
    paddingHorizontal: 20,
    minWidth: 70,
  },
  // V3: Disabled state for submit button
  buttonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  // Web hover/focus feedback — subtle lift on the filled Send button
  buttonSmallHovered: {
    opacity: 0.92,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.15,
    shadowRadius: 6,
    elevation: 2,
  },
  // Admin-only comment badge
  adminOnlyBadge: {
    backgroundColor: AppStyles.colorSet.warningLight,
    paddingVertical: 2,
    paddingHorizontal: 8,
    borderRadius: AppStyles.borderRadii.xs,
  },
  // Comment grouping
  commentGroup: {
    marginBottom: 20,
  },
  adminOnlyGroup: {
    backgroundColor: AppStyles.colorSet.inputBackground,
    padding: 16,
    borderRadius: 12,
    borderLeftWidth: 3,
    borderLeftColor: AppStyles.colorSet.warning,
  },
  // Admin-only checkbox row inline with Send button
  adminOnlyCheckboxRow: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  adminOnlyCheckboxText: {
    marginLeft: 2,
  },
  // Comment edit/delete actions
  commentActions: {
    flexDirection: 'row',
    marginLeft: 12,
    gap: 8,
  },
  commentActionButton: {
    padding: 4,
  },
  // Web hover/focus feedback for the icon-only edit/delete comment buttons
  commentActionButtonHovered: {
    backgroundColor: AppStyles.stateSet.hover,
    borderRadius: 6,
  },
  // Edit mode styles
  editContainer: {
    marginTop: hp(0.5),
  },
  editInput: {
    backgroundColor: '#fff',
    borderWidth: 1,
    borderColor: AppStyles.colorSet.brandPrimary,
    borderRadius: 8,
    padding: 10,
    fontFamily: fonts.POPPINS.regular,
    fontSize: 15,
    color: '#000',
    minHeight: 60,
  },
  editActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 8,
    marginTop: 8,
  },
  editButton: {
    paddingVertical: 6,
    paddingHorizontal: 12,
    borderRadius: 6,
    minWidth: 60,
    alignItems: 'center',
  },
  editButtonCancel: {
    backgroundColor: '#E5E5E5',
  },
  editButtonSave: {
    backgroundColor: '#FFC451',
  },
  // Web hover/focus feedback for the edit Cancel/Save buttons
  editButtonHovered: {
    opacity: 0.88,
  },
});

export default ReportComments;
